import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { candidateSummaryOf } from './RowDrawerCandidates';
import type { RowDrawerPickedVariant } from './rowDrawerResolutions';

type RowDrawerPickProps = {
    pick: RowDrawerPickedVariant;
    onClear: () => void;
};

/**
 * The row is an explicit restock of a variant the user picked from the
 * candidates: an accent alert saying so, with the variant as the card showed
 * it (when the drawer still knows it), and **Clear pick** under it (beside
 * the text, a phone's Georgian label left the text a narrow column), which
 * takes the pick back so the next check offers the candidates again.
 */
export const RowDrawerPick: React.FC<RowDrawerPickProps> = ({
    pick,
    onClear,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Alert
            status="accent"
            {...getNodeTestIdProp('catalog-import-row-drawer-pick')}
        >
            <Alert.Indicator />
            <Alert.Content>
                <Alert.Title>
                    {t('catalogImport.products.drawer.pick.title')}
                </Alert.Title>
                {pick.candidate === null ? null : (
                    <Alert.Description>
                        {candidateSummaryOf(t, pick.candidate)}
                    </Alert.Description>
                )}
                <Button
                    className="mt-2"
                    onPress={onClear}
                    size="sm"
                    variant="tertiary"
                    {...getNodeTestIdProp(
                        'catalog-import-row-drawer-clear-pick',
                    )}
                >
                    {t('catalogImport.products.drawer.pick.clear')}
                </Button>
            </Alert.Content>
        </Alert>
    );
};
